import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { isExternalHref } from "../lib/notifications";
import { ROUTES } from "../lib/routes";

interface NavLink {
  id: string;
  label: string;
  to: string;
}

// Industry Collaborations (/international) and Clubs & Forums (/clubs) are
// deliberately absent from nav — hidden, not removed. The pages and routes
// still work; see HIDDEN_PAGES.md for the registry and why.
const PRIMARY_LINKS: NavLink[] = [
  { id: "academics", label: "Academics", to: ROUTES.academics },
  { id: "admissions", label: "Admissions", to: ROUTES.admissions },
  { id: "research", label: "Research", to: ROUTES.research },
  { id: "campus", label: "Campus Life", to: ROUTES.campusLife },
  { id: "about", label: "About", to: ROUTES.about },
  { id: "contact", label: "Contact", to: ROUTES.contact },
];

interface MegaCategory {
  id: string;
  title: string;
  /** One-line copy under the heading. */
  blurb: string;
  links: NavLink[];
}

const MEGA_CATEGORIES: MegaCategory[] = [
  {
    id: "academics",
    title: "Academics",
    blurb: "Courses, faculty and examinations.",
    links: [
      { id: "faculty", label: "Faculty", to: ROUTES.faculty },
      { id: "examinations", label: "Examinations", to: ROUTES.examinations },
    ],
  },
  {
    id: "student-life",
    title: "Student Life",
    blurb: "Where students live, learn and gather.",
    links: [
      { id: "gallery", label: "Gallery", to: ROUTES.gallery },
      { id: "hostels", label: "Hostels & Mess", to: ROUTES.hostels },
      { id: "library", label: "Library", to: ROUTES.library },
      { id: "scholarships", label: "Scholarships", to: ROUTES.scholarships },
    ],
  },
  {
    id: "career",
    title: "Career & Network",
    blurb: "Placement record, alumni and recruiter portal.",
    links: [
      { id: "placements", label: "Placements", to: ROUTES.placements },
      { id: "alumni", label: "Alumni", to: ROUTES.alumni },
      { id: "recruiters", label: "For Recruiters", to: ROUTES.recruiters },
    ],
  },
  {
    id: "press",
    title: "Events & Notices",
    blurb: "What's happening, and where to check for updates.",
    links: [
      { id: "events", label: "Events", to: ROUTES.events },
      { id: "news", label: "News", to: ROUTES.news },
      { id: "notifications", label: "Notifications", to: ROUTES.notifications },
      { id: "visitors", label: "Distinguished Visitors", to: ROUTES.visitors },
    ],
  },
  {
    id: "governance",
    title: "Governance",
    blurb: "Compliance, disclosures and our environmental commitments.",
    links: [
      { id: "committees", label: "Committees", to: ROUTES.committees },
      { id: "disclosures", label: "Mandatory Disclosures", to: ROUTES.disclosures },
    ],
  },
];

const MORE_LINKS: NavLink[] = MEGA_CATEGORIES.flatMap((c) => c.links);

/**
 * The audience switcher in the top bar.
 *
 * Students and Staff point at the institute's real login portals, published
 * alongside the results on bietdvg.edu/autonomous-1yr. They live here rather
 * than on the examinations page because they are audience entry points, not
 * examination content — a student signs in to the portal for far more than
 * results.
 *
 * Parents, Alumni and Career were unresolved `#` placeholders and have been
 * hidden here until they have real destinations. The render below picks the
 * right element per entry: `#` stays a bare anchor, an absolute URL opens in
 * a new tab, and an in-app path routes.
 */
const UTILITY_LINKS = [
  { label: "Social Media Hub", href: `${ROUTES.contact}#social` },
  { label: "Students", href: "https://shareu.rovelabs.com/login" },
  { label: "Staff", href: "https://academics.rovelabs.com/login" },
];

interface HeaderProps {
  /** Active nav item id — matches the `id` field on PRIMARY_LINKS / MORE_LINKS. */
  active?: string;
}

export function Header({ active }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const allLinks = [...PRIMARY_LINKS, ...MORE_LINKS];

  // Resolve active id from the current pathname when not given.
  const resolvedActive =
    active ??
    allLinks.find((l) => l.to === pathname)?.id ??
    (pathname === "/" ? "home" : "");

  const inMore = MORE_LINKS.some((m) => m.id === resolvedActive);

  // The utility bar — admission codes and audience links — is a landing-page
  // banner, so it rides only on the home page. Every other route starts at the
  // main nav.
  const showUtilityBar = pathname === ROUTES.home;

  // The utility bar collapses in lockstep with scrolling through the hero,
  // rather than snapping on an intersection threshold and animating on its
  // own timer — a fixed-duration transition reads as sluggish on a fast
  // scroll and twitchy on a slow one, since it's racing the scroll instead of
  // tracking it. `collapse` is 0 (fully open) to 1 (fully collapsed),
  // recomputed every animation frame from how far the hero has scrolled past
  // under the header, so the bar's height always matches scroll position.
  const [collapse, setCollapse] = useState(0);
  useEffect(() => {
    if (!showUtilityBar) return;
    const heroEl = document.querySelector("[data-hero-root]");
    if (!heroEl) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    const update = () => {
      const heroHeight = heroEl.getBoundingClientRect().height || 1;
      const scrolledPast = Math.min(Math.max(-heroEl.getBoundingClientRect().top, 0), heroHeight);
      const ratio = scrolledPast / heroHeight;
      setCollapse(reduceMotion ? (ratio >= 1 ? 1 : 0) : ratio);
    };
    update();
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [showUtilityBar]);

  // --header-h drives everything that has to sit clear of the sticky header:
  // the context bar's `top`, the sticky side navs, and the scroll-margin on
  // every anchor target. It used to be a fixed 108px, which was the height
  // *with* the utility bar — so once that bar became home-only, the header on
  // every other route measured 82px and the context bar hung 26px below it with
  // a strip of page showing through. Measuring it keeps the two in contact on
  // every route, at every breakpoint, and when the bar wraps.
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--header-h",
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    apply();
    // ResizeObserver catches the utility bar wrapping at narrow widths, which a
    // resize listener alone would miss; jsdom has no ResizeObserver, so fall
    // back to the listener there rather than throwing on mount.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showUtilityBar]);

  return (
    <header className="header" ref={headerRef}>
      {showUtilityBar && (
        <div
          className="utility-bar"
          style={{ maxHeight: `${60 * (1 - collapse)}px`, opacity: 1 - collapse }}
        >
          <div className="container">
            <div className="utility-codes">
              <span>
                KCET <b>B.E · E062</b>
              </span>
              <span>
                COMEDK <b>B.E · E021</b>
              </span>
              <span>
                PGCET <b>MCA · C413</b>
              </span>
              <span>
                PGCET <b>M.Tech · T813</b>
              </span>
              {/* No college code published for the KMAT route yet — the other
                  three carry one, so this reads programme-only until it is. */}
              <span>
                KMAT <b>MCA</b>
              </span>
            </div>
            <div className="utility-links">
              {UTILITY_LINKS.map((u) =>
                isExternalHref(u.href) ? (
                  // A login portal leaves the site; <Link> would treat the
                  // absolute URL as an in-app path and hit the wildcard route.
                  <a
                    key={u.label}
                    href={u.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${u.label} login (external site)`}
                  >
                    {u.label}
                  </a>
                ) : u.href === "#" ? (
                  <a key={u.label} href={u.href}>
                    {u.label}
                  </a>
                ) : (
                  <Link key={u.label} to={u.href}>
                    {u.label}
                  </Link>
                ),
              )}
            </div>
          </div>
        </div>
      )}
      <nav className="main-nav">
        <div className="container">
          <Link className="brand" to={ROUTES.home}>
            <div className="brand-mark">
              <img src="/biet-logo.png" alt="BIET logo" />
            </div>
            <div>
              <div className="brand-name">Bapuji Institute of Engineering and Technology</div>
            </div>
          </Link>
          <ul className="nav-links">
            {PRIMARY_LINKS.map((l) => (
              <li key={l.id}>
                <Link to={l.to} className={resolvedActive === l.id ? "active" : ""}>
                  {l.label}
                </Link>
              </li>
            ))}
            <li className="nav-more">
              <a
                href="#"
                className={inMore ? "active" : ""}
                onClick={(e) => e.preventDefault()}
              >
                More
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginLeft: 4, verticalAlign: "middle" }}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </a>
              <div className="mega-menu" role="menu" aria-label="More navigation">
                <div className="mega-menu-grid">
                  {MEGA_CATEGORIES.map((cat) => (
                    <div key={cat.id} className="mega-col">
                      <div className="mega-col-head">
                        <span className="mega-col-title">{cat.title}</span>
                      </div>
                      <p className="mega-col-blurb">{cat.blurb}</p>
                      <ul className="mega-col-links">
                        {cat.links.map((l) => (
                          <li key={l.id}>
                            <Link
                              to={l.to}
                              className={resolvedActive === l.id ? "active" : ""}
                            >
                              <span className="mega-link-label">{l.label}</span>
                              <span className="mega-link-arr" aria-hidden="true">
                                →
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="mega-foot">
                  <span className="mega-foot-label">Quick links</span>
                  <Link to={ROUTES.admissions}>Admissions 2026 — 27</Link>
                  <Link to={ROUTES.placements}>Placement report</Link>
                  <Link to={ROUTES.contact}>Contact</Link>
                </div>
              </div>
            </li>
          </ul>
          <div className="nav-actions">
            <button
              className={"nav-toggle " + (mobileOpen ? "is-open" : "")}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>
      <div
        className={"mobile-drawer " + (mobileOpen ? "is-open" : "")}
        aria-hidden={!mobileOpen}
      >
        <div className="mobile-drawer-inner">
          <ul className="mobile-nav-links">
            {allLinks.map((l) => (
              <li key={l.id}>
                <Link
                  to={l.to}
                  className={resolvedActive === l.id ? "active" : ""}
                  onClick={() => setMobileOpen(false)}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mobile-drawer-utility">
            {UTILITY_LINKS.map((u) => (
              <a key={u.label} href={u.href}>
                {u.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
